import axios from 'axios'
import Swal from 'sweetalert2'
import { requireSession, reportSessionError, reportSsoEvent, flushSsoEvents, logoutSharedSession } from './shared-session'

let pending = false
let releaseWaiting = null
export const LEGACY_PERFORMANCE_PATH = '/member/performance-search'

const storedAccessToken = () => {
  try {
    const token = JSON.parse(localStorage.getItem('token') || 'null')?.access_token
    return typeof token === 'string' && /^Bearer \S+$/i.test(token) ? token : ''
  } catch (_) { return '' }
}
const api = axios.create({ baseURL: process.env.VUE_APP_BASE_URL, timeout: 15000 })

export const isValidSsoTrace = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value)

export const createSsoTrace = () => {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export const isAllowedSsoUrl = (value) => {
  try {
    const url = new URL(value)
    const allowed = ['https://pv.tocin.com.tw/Account/Login', 'http://211.75.213.163:8082/tocin_pv/Account/Login']
    const entries = Array.from(url.searchParams.entries())
    return allowed.includes(url.origin + url.pathname) &&
      !url.username && !url.password && !url.hash &&
      entries.length === 2 && url.searchParams.getAll('sso_token').length === 1 && url.searchParams.getAll('trace_id').length === 1 &&
      /^tocin_sso_[A-Za-z0-9_-]{43}$/.test(url.searchParams.get('sso_token')) && isValidSsoTrace(url.searchParams.get('trace_id'))
  } catch (_) {
    return false
  }
}

const messages = {
  SSO_TEST_NOT_ALLOWED: '目前會員未開放 SSO 測試，請重新點擊業績查詢。',
  MEMBER_NOT_ALLOWED: '目前會員狀態無法使用業績查詢，請洽東震客服。',
  CAPABILITY_STALE: '業績查詢同步登出服務尚未就緒，請稍後再試。',
  WEB_SSO_RELOGIN_REQUIRED: '請重新登入東震會員，啟用兩站同步登出後再進入業績查詢。',
  SSO_UNAVAILABLE: '業績查詢免登入暫停服務，請稍後再試。',
  TRACE_INVALID: '追蹤碼格式錯誤，請重新點擊業績查詢。',
  TRACE_CONFLICT: '此追蹤碼已使用，請重新點擊業績查詢。',
  INVALID_REQUEST: '無法完成請求，請重新操作。'
}

export default {
  data () {
    return { ssoBusy: false, ssoShowLegacy: false, ssoEntryVersion: null, ssoEntryMode: 'legacy', ssoEntryReady: false, ssoEntryToken: '', ssoEntryTimer: null, ssoEntryChecking: false, ssoEntryRequest: 0 }
  },
  computed: {
    performanceEntryHref () { return LEGACY_PERFORMANCE_PATH }
  },
  watch: {
    'UserData.loginStatus' () { this.refreshSsoEntry() },
    '$route.fullPath' () { this.refreshSsoEntry() }
  },
  mounted () {
    this.refreshSsoEntry()
    this.ssoEntryTimer = window.setInterval(() => this.refreshSsoEntry(), 10000)
    window.addEventListener('focus', this.refreshSsoEntry)
    window.addEventListener('storage', this.refreshSsoEntry)
  },
  beforeDestroy () {
    if (this.ssoBusy && releaseWaiting) releaseWaiting()
    this.ssoEntryRequest++
    window.clearInterval(this.ssoEntryTimer)
    window.removeEventListener('focus', this.refreshSsoEntry)
    window.removeEventListener('storage', this.refreshSsoEntry)
  },
  methods: {
    async refreshSsoEntry (event) {
      // Idle timestamps in another tab do not change SSO eligibility.
      if (event?.type === 'storage' && event.key !== 'token' && event.key !== null) return
      const token = storedAccessToken()
      const request = ++this.ssoEntryRequest
      if (token !== this.ssoEntryToken) {
        this.ssoEntryReady = false
        this.ssoEntryMode = 'legacy'
        this.ssoShowLegacy = false
      }
      this.ssoEntryToken = token
      if (!token) {
        this.ssoEntryMode = 'legacy'
        this.ssoShowLegacy = false
        this.ssoEntryReady = true
        return
      }
      if (this.ssoEntryChecking) { this.ssoEntryRequest--; return }
      this.ssoEntryChecking = true
      try {
        const { data } = await api.get('haotian-sso/entry', {
          headers: { Authorization: token, Accept: 'application/json', 'Cache-Control': 'no-cache' }
        })
        if (request === this.ssoEntryRequest && token === storedAccessToken()) {
          this.ssoEntryVersion = data.settings_version
          this.ssoEntryMode = data.mode === 'sso' ? 'sso' : 'legacy'
          this.ssoShowLegacy = data.mode === 'sso' && data.show_legacy_entry === true
          this.ssoEntryReady = true
        }
      } catch (error) {
        if (request === this.ssoEntryRequest && token === storedAccessToken()) {
          this.ssoEntryReady = false
          this.ssoShowLegacy = false
          if (error.response?.status === 401) {
            localStorage.removeItem('token')
            this.ssoEntryToken = ''
            this.ssoEntryMode = 'legacy'
            this.ssoEntryReady = true
          }
        }
      } finally {
        this.ssoEntryChecking = false
      }
    },
    async openHaotianSso () {
      if (pending) return
      const accessToken = storedAccessToken()
      if (!accessToken || !/^Bearer \S+$/i.test(accessToken)) {
        await Swal.fire({ text: '請先登入東震會員，再使用業績查詢。', confirmButtonText: '前往登入' })
        window.location.assign('/member/login')
        return
      }
      // Resolve eligibility before navigating in this window.
      if (!this.ssoEntryReady || this.ssoEntryToken !== accessToken) {
        await this.refreshSsoEntry()
        if (storedAccessToken() !== accessToken) {
          if (!storedAccessToken()) window.location.assign('/member/login')
          return
        }
        if (!this.ssoEntryReady) {
          await Swal.fire({ text: '暫時無法確認登入狀態，請稍後重新點擊業績查詢。' })
          return
        }
      }
      if (this.ssoEntryMode !== 'sso') {
        window.location.assign(LEGACY_PERFORMANCE_PATH)
        return
      }
      let traceId
      try { traceId = createSsoTrace() } catch (_) {
        await Swal.fire({ text: '無法建立登入追蹤碼，請使用支援安全亂數的瀏覽器重試。' })
        return
      }
      pending = true
      this.ssoBusy = true
      const waitingResult = Swal.fire({
        title: '業績查詢',
        text: '正在為您登入業績查詢系統，請稍候…',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      })
      const waitingPopup = Swal.getPopup()
      const closeWaiting = () => {
        if (Swal.getPopup() === waitingPopup) Swal.close()
      }
      let navigating = false
      let navigationTimer
      const release = () => {
        window.clearTimeout(navigationTimer)
        window.removeEventListener('pageshow', onPageShow)
        closeWaiting()
        pending = false
        this.ssoBusy = false
        if (releaseWaiting === release) releaseWaiting = null
      }
      const onPageShow = event => {
        // Back/forward cache restores the old document, including its open dialog.
        if (event.persisted) release()
      }
      releaseWaiting = release
      try {
        await requireSession()
        reportSsoEvent('entry_requested', traceId, {}, accessToken)
        const started = performance.now()
        const response = await api.post('haotian-sso/token', {}, {
          headers: { Authorization: accessToken, Accept: 'application/json', 'Content-Type': 'application/json', 'X-SSO-Trace-Id': traceId }
        })
        const { data } = response
        if (!isAllowedSsoUrl(data.redirect_url) || data.trace_id !== traceId || new URL(data.redirect_url).searchParams.get('trace_id') !== traceId) throw Object.assign(new Error('Invalid SSO destination'), { code: 'INVALID_SSO_DESTINATION' })
        if (storedAccessToken() !== accessToken) return
        reportSsoEvent('token_received', traceId, { result: 'success', response, duration_ms: performance.now() - started }, accessToken)
        reportSsoEvent('redirect_started', traceId, {}, accessToken)
        await flushSsoEvents(accessToken, 500)
        if (storedAccessToken() !== accessToken) return
        const returnUrl = window.location.href
        window.addEventListener('pageshow', onPageShow)
        window.location.assign(data.redirect_url)
        navigating = true
        // Keep covering the old page while the destination responds. A slow or
        // cancelled navigation must still give the member a way back.
        navigationTimer = window.setTimeout(() => {
          if (releaseWaiting !== release || Swal.getPopup() !== waitingPopup) return
          Swal.hideLoading()
          Swal.update({
            text: '業績查詢連線較久，您可繼續等候，或返回官網後重試。',
            showConfirmButton: true,
            confirmButtonText: '返回官網'
          })
        }, 30000)
        waitingResult.then(result => {
          if (result.isConfirmed && releaseWaiting === release) {
            release()
            window.location.replace(returnUrl)
          }
        })
      } catch (error) {
        closeWaiting()
        const status = error.response && error.response.status
        const code = error.response && error.response.data && error.response.data.error_code
        const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
        if (storedAccessToken() !== accessToken) return
        const diagnosticCode = /^[A-Z0-9_]{1,40}$/.test(code || '') ? code : (timedOut ? 'REQUEST_TIMEOUT' : (/^[A-Z0-9_]{1,40}$/.test(error.code || '') ? error.code : 'NETWORK_UNAVAILABLE'))
        reportSessionError(diagnosticCode, traceId, accessToken, error.response)
        if (code === 'WEB_SSO_RELOGIN_REQUIRED') {
          const result = await Swal.fire({ text: messages[code], showCancelButton: true, confirmButtonText: '重新登入', cancelButtonText: '取消' })
          if (result.isConfirmed && storedAccessToken() === accessToken) {
            try {
              if (await logoutSharedSession()) window.location.assign('/member/login')
            } catch (_) { /* logoutSharedSession already explains the failure. */ }
          }
        } else if (status === 401) {
          localStorage.removeItem('token')
          await Swal.fire({ text: '登入狀態已失效，請重新登入後再試。' })
        } else {
          const text = error.code === 'SESSION_UNCONFIRMED'
            ? '暫時無法確認業績查詢的登入狀態，請稍候重新點擊業績查詢。'
            : timedOut
              ? '連線逾時，請返回東震官網後重新點擊業績查詢。'
              : messages[code] || (status === 429
                ? '操作過於頻繁，請返回東震官網，稍候再試。'
                : '暫時無法連線至業績查詢，請返回東震官網後重試。')
          await Swal.fire({ text })
        }
        if (code === 'SSO_TEST_NOT_ALLOWED' || code === 'SSO_UNAVAILABLE' || code === 'CAPABILITY_STALE' || code === 'WEB_SSO_RELOGIN_REQUIRED' || status === 401) this.refreshSsoEntry()
      } finally {
        // assign() starts navigation; it does not wait for the new page to load.
        if (!navigating) release()
      }
    }
  }
}
